import type { DropdownSelectOption } from '@paintscout/ui'
import { Collapse, FilterChipGroup, Grid, InputLabel, Paper, useClientOptions, useUser } from '@paintscout/ui'
import type { InFilter, RangeFilter, ValueFilter } from 'json-to-lucene'
import React from 'react'
import { makeStyles } from '@material-ui/core'
import classnames from 'classnames'
import {
  formatCurrency,
  formatName,
  getDealStageOptions,
  getObjectLabels,
  getQuoteTags,
  getQuoteTypeOptions
} from '@paintscout/util/builder'
import { useSearchUsersQuery } from '@paintscout/api'
import { removeAuth0Prefix } from '@paintscout/util/users'
import moment from 'moment'
import { useFlags } from 'shared/components/common/FlagsProvider'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  chipGroup: {
    margin: theme.spacing(1, 0),
    gridTemplateColumns: `repeat(auto-fill, minmax(120px, 1fr))`,
    '&:last-child': {
      marginBottom: 0
    }
  },
  longChip: {
    gridTemplateColumns: `1fr`
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    marginRight: theme.spacing(),
    backgroundColor: theme.palette.quote.status.draft,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}))

interface DealSearchFilterChipsProps {
  show: boolean
  filters: ValueFilter[]
  onDelete: (filter: ValueFilter, valueIndex: number) => void
  pipeline: string
}

export default function DealSearchFilterChips(props: DealSearchFilterChipsProps) {
  const { show, filters, onDelete, pipeline } = props
  const classes = useStyles()
  const { options, clientId } = useClientOptions()
  const { quote: quoteLabel } = getObjectLabels({ options })

  const { isSuperadmin } = useUser()
  const query = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''
  const { data, loading: usersLoading } = useSearchUsersQuery({
    variables: {
      query
    },
    fetchPolicy: 'cache-and-network'
  })

  const { canDeactivateSettingsItems } = useFlags()
  const dateFormat = 'MMM D, YYYY'
  const quoteTypeOptions = getQuoteTypeOptions({ options, canDeactivate: !!canDeactivateSettingsItems })
  const quoteTypeFilter = filters.find((filter) => filter.id === 'quoteType') as InFilter
  const activeQuoteTypeOptions = (quoteTypeFilter?.value ?? []).map((value) =>
    quoteTypeOptions.find((option) => option.key === value)
  )

  const stageOptions = getDealStageOptions({ options, pipeline })

  const stageFilter = filters.find((filter) => filter.id === 'stage.value') as InFilter
  const activeStageOptions = (stageFilter?.value ?? []).map((value) =>
    stageOptions.find((option) => option.key === value)
  )

  const estimatorFilter = filters.find((filter) => filter.id === 'owner.id') as InFilter
  const estimatorOptions = (data?.searchUsers?.rows || []).map((user) => ({
    label: user.name || (user.user_metadata ? formatName(user.user_metadata) : ''),
    value: removeAuth0Prefix(user.user_id)
  }))
  const activeEstimatorOptions = (estimatorFilter?.value ?? []).map((value) =>
    estimatorOptions.find((option) => option.value === value)
  )

  const dateFilter = filters.find((filter) => filter.id === 'created') as RangeFilter
  const amountFilter = filters.find((filter) => filter.id === 'value') as RangeFilter

  const archivedOptions: DropdownSelectOption[] = [
    { label: 'No Archived', value: ['false'] },
    { label: 'Include Archived', value: ['true', 'false'] },
    { label: 'Only Archived', value: ['true'] }
  ]
  const archivedFilter = filters.find((filter) => filter.id === 'archived')
  const activeArchivedOption = archivedOptions.find(
    (option) =>
      option.value?.length === (archivedFilter?.value as string[])?.length &&
      option.value.every((value) => (archivedFilter?.value as string[])?.includes(value))
  )

  const quoteTagOptions = getQuoteTags({ options })
  const tagsFilter = filters.find((filter) => filter.id === 'tags') as InFilter
  const activeTagsOptions = (tagsFilter?.value ?? []).map((value) =>
    quoteTagOptions.find((option) => option.name === value)
  )

  const getDateFilterChipValue = () => {
    if (!dateFilter?.value) {
      return ''
    }
    if (dateFilter.value[0] && !dateFilter.value[1]) {
      return `After ${moment(dateFilter.value[0]).format(dateFormat)}`
    }
    if (!dateFilter.value[0] && dateFilter.value[1]) {
      return `Before ${moment(dateFilter.value[1]).format(dateFormat)}`
    }
    return `${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(dateFilter.value[1]).format(dateFormat)}`
  }

  const getAmountFilterChipValue = () => {
    if (!amountFilter?.value) {
      return ''
    }
    if (amountFilter.value[0] && !amountFilter.value[1]) {
      return `Over ${formatCurrency({ value: amountFilter.value[0], options })}`
    }
    if (!amountFilter.value[0] && amountFilter.value[1]) {
      return `Under ${formatCurrency({ value: amountFilter.value[1], options })}`
    }
    return `${formatCurrency({ value: amountFilter.value[0], options })} - ${formatCurrency({ value: amountFilter.value[1], options })}`
  }

  return (
    <div className={classes.root}>
      <Collapse show={show}>
        <Paper className={classnames(classes.root, classes.paper)}>
          <Grid container spacing={5}>
            {!!activeQuoteTypeOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>{quoteLabel.value} Type</InputLabel>
                <FilterChipGroup
                  classes={{
                    root: classnames(classes.chipGroup, activeQuoteTypeOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeQuoteTypeOptions.map((option, index) => ({
                    label: option.label,
                    onDelete: () => onDelete(quoteTypeFilter, index)
                  }))}
                />
              </Grid>
            )}
            {!!activeStageOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Deal Stage</InputLabel>
                <FilterChipGroup
                  classes={{
                    root: classnames(classes.chipGroup, activeStageOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeStageOptions.map((option, index) => ({
                    label: option.label,
                    onDelete: () => onDelete(stageFilter, index)
                  }))}
                />
              </Grid>
            )}
            {!!activeEstimatorOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Owner</InputLabel>
                <FilterChipGroup
                  loading={usersLoading}
                  classes={{
                    root: classnames(classes.chipGroup, activeEstimatorOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeEstimatorOptions.map((option, index) => ({
                    label: option?.label,
                    onDelete: () => onDelete(estimatorFilter, index)
                  }))}
                />
              </Grid>
            )}
            {!!dateFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Created Date</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getDateFilterChipValue(),
                      onDelete: () => onDelete(dateFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!amountFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Amount</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getAmountFilterChipValue(),
                      onDelete: () => onDelete(amountFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!activeArchivedOption && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Archived</InputLabel>
                <FilterChipGroup
                  classes={{ root: classnames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: activeArchivedOption?.label,
                      onDelete: () => onDelete(archivedFilter, 0)
                    }
                  ]}
                />
              </Grid>
            )}
            {!!activeTagsOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Tags</InputLabel>
                <FilterChipGroup
                  classes={{
                    root: classnames(classes.chipGroup, activeTagsOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeTagsOptions.map((option, index) => ({
                    label: option.label,
                    color: option.color,
                    onDelete: () => onDelete(tagsFilter, index)
                  }))}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Collapse>
    </div>
  )
}
